import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useFetchCampaignIndividualSequencingContent,
  useFetchCampaignLeads,
  useContentWriterStatus,
  useRegenerateCampaignIndividualSequencingContent,
  useUpdateCampaignIndividualSequencingContent,
} from "@/features/campaigns/hooks/useCampaign";
import { useCampaignId } from "@/features/campaigns/hooks/useCampaignId";
import Loader from "@/shared/components/ui/Loaders/Loader";
import NoDataCommonCard from "@/shared/components/ui/NoDataView/NoDataCommon";
import noLeadsImage from "@/assets/images/onboarding3.gif";
import NoLeadsSkeleton from "./NoLeadsSkeleton";
import IndividualSequenceTrigger from "./IndividualSequenceTrigger";
import IndividualSequenceContentArea from "./IndividualSequenceContentArea";
import {
  isContentStillGenerating,
  isProfilePending,
  shouldPollSequenceContent,
} from "@/features/campaigns/utils/individualSequenceContentStatus";

const IndividualSequenceContent = () => {
  const navigate = useNavigate();
  const campaignId = useCampaignId();
  const [searchParams, setSearchParams] = useSearchParams();

  // The full list backs prev/next navigation and the leadId -> index lookup, so
  // it is fetched in one page. It is navigation data, not a live counter: the
  // 15s poll would re-download the whole campaign's leads for nothing.
  const { data: campaignLeads, isLoading: isLoadingCampaignLeads } =
    useFetchCampaignLeads({
      campaignId,
      limit: 10000,
      enabled: true,
      refetchInterval: false,
    });

  const leads = campaignLeads?.leads || [];

  const currentLeadIndex = useMemo(() => {
    if (!leads.length) return 0;
    const leadId = searchParams.get("leadId");
    if (!leadId) return 0;
    const idx = leads.findIndex((l) => l.contact_id === leadId);
    return idx !== -1 ? idx : 0;
  }, [leads, searchParams]);

  const totalLeadsCount = campaignLeads?.total ?? leads.length;
  const currentLead = leads[currentLeadIndex] || null;

  const {
    data: campaignIndividualSequencingContent,
    isLoading: isLoadingCampaignIndividualSequencingContent,
    isFetching: isFetchingCampaignIndividualSequencingContent,
  } = useFetchCampaignIndividualSequencingContent({
    campaignId: campaignId,
    prospectId: currentLead?.contact_id,
    enrollmentId: currentLead?.id,
    enabled: !!campaignId && !!currentLead?.contact_id,
    refetchInterval: (query) => {
      const data = query?.state?.data;
      if (!shouldPollSequenceContent(data)) return false;
      return isContentStillGenerating(data) ? 3000 : 5000;
    },
  });

  /**
   * profilePending means the server answered fast and left the triggers to a
   * later poll. Without folding it in here the panel falls through to "No
   * signals available", which reads as "this prospect has none" rather than
   * "still loading".
   */
  // Campaigns made only of fixed messages run no AI research, so there is nothing to wait for.
  const isAiResearchOff = campaignIndividualSequencingContent?.aiResearch === false;
  const isSignalsLoading =
    !isAiResearchOff &&
    (isLoadingCampaignIndividualSequencingContent ||
      isProfilePending(campaignIndividualSequencingContent));

  const contactId = currentLead?.contact_id;

  const { data: contentWriterStatus } = useContentWriterStatus({
    campaignId,
    prospectId: contactId,
    enrollmentId: currentLead?.id,
    enabled: !!campaignId && !!contactId,
  });

  const {
    mutate: updateCampaignIndividualSequencingContent,
    isPending: isUpdatingCampaignIndividualSequencingContent,
  } = useUpdateCampaignIndividualSequencingContent();

  const {
    mutate: regenerateCampaignIndividualSequencingContent,
    isPending: isRegeneratingFromTrainerMemory,
  } = useRegenerateCampaignIndividualSequencingContent();

  const showTrainerUpdatePrompt =
    contentWriterStatus?.has_cached === true &&
    contentWriterStatus?.has_update === true;

  const handleUpdate = (payload) => {
    updateCampaignIndividualSequencingContent(payload);
  };

  const handleTrainerMemoryUpdate = () => {
    if (!campaignId || !contactId) return;
    regenerateCampaignIndividualSequencingContent({
      campaignId,
      prospectId: contactId,
      enrollmentId: currentLead?.id,
    });
  };

  const handleSelectLead = (index) => {
    if (index >= 0 && index < leads.length) {
      const selectedLead = leads[index];
      if (selectedLead?.contact_id) {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set("leadId", selectedLead.contact_id);
          return next;
        }, { replace: true });
      }
    }
  };

  const handleNextLead = () => {
    if (currentLeadIndex < leads.length - 1) {
      handleSelectLead(currentLeadIndex + 1);
    }
  };

  const handlePreviousLead = () => {
    if (currentLeadIndex > 0) {
      handleSelectLead(currentLeadIndex - 1);
    }
  };

  const handleAssignLeads = () => {
    navigate(`/contacts`);
  };

  if (isLoadingCampaignLeads) {
    return (
      <div className="w-full h-[calc(100dvh-171px)] flex items-center justify-center">
        <Loader text="Loading leads..." />
      </div>
    );
  }

  if (leads.length === 0) {
    return (
      <div className="w-full relative">
        <NoLeadsSkeleton />
        <NoDataCommonCard
          title="No Leads Assigned"
          description="No leads assigned to this campaign"
          buttonText="Assign Leads"
          onClick={handleAssignLeads}
          image={noLeadsImage}
        />
      </div>
    );
  }

  return (
    <div className="w-full gap-4 flex h-[calc(100dvh-140px)]">
      <IndividualSequenceTrigger
        currentLead={currentLead}
        individualSequencing={
          campaignIndividualSequencingContent?.individualSequencing
        }
        isContentGenerated={
          campaignIndividualSequencingContent?.isContentGenerated
        }
        leads={leads}
        totalLeadsCount={totalLeadsCount}
        currentLeadIndex={currentLeadIndex}
        isLoadingIndividualSequencing={isSignalsLoading}
        isAiResearchOff={isAiResearchOff}
        contentGeneration={campaignIndividualSequencingContent?.contentGeneration}
        isUpdating={isUpdatingCampaignIndividualSequencingContent}
        onNextLead={handleNextLead}
        onPreviousLead={handlePreviousLead}
        onSelectLead={handleSelectLead}
        onUpdate={handleUpdate}
      />
      <IndividualSequenceContentArea
        content={campaignIndividualSequencingContent?.result}
        sequenceContent={campaignIndividualSequencingContent}
        isLoading={isLoadingCampaignIndividualSequencingContent}
        isFetching={isFetchingCampaignIndividualSequencingContent}
        isUpdating={isUpdatingCampaignIndividualSequencingContent}
        currentLead={currentLead}
        showTrainerUpdatePrompt={showTrainerUpdatePrompt}
        onTrainerMemoryUpdate={handleTrainerMemoryUpdate}
        isTrainerMemoryUpdating={isRegeneratingFromTrainerMemory}
      />
    </div>
  );
};

export default IndividualSequenceContent;
